import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface VerificationStatusProps {
  memberName: string;
}

const statusSteps = [
  {
    id: 'vstatus-submitted',
    icon: 'DocumentArrowUpIcon',
    label: 'Documents Submitted',
    description: 'Your documents and ₹500 payment have been received successfully.',
    status: 'completed',
    date: 'Today',
  },
  {
    id: 'vstatus-review',
    icon: 'MagnifyingGlassIcon',
    label: 'Under Review',
    description: 'Our FCEP verification team is reviewing your documents. This takes 2–3 working days.',
    status: 'current',
    date: 'In Progress',
  },
  {
    id: 'vstatus-verified',
    icon: 'BadgeCheckIcon',
    label: 'Verified Member',
    description: 'You will receive an SMS and email confirmation once verification is complete.',
    status: 'pending',
    date: 'Estimated: 2–3 Days',
  },
];

export default function VerificationStatus({ memberName }: VerificationStatusProps) {
  return (
    <div className="fade-in">
      {/* Success Banner */}
      <div className="bg-card border border-border rounded-2xl p-8 text-center mb-6">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircleIcon" size={44} variant="solid" className="text-secondary" />
        </div>
        <h2 className="text-2xl font-700 text-foreground mb-2">
          Verification Submitted Successfully!
        </h2>
        <p className="text-muted-foreground text-base max-w-md mx-auto">
          Thank you, <strong className="text-foreground">{memberName}</strong>! Your documents
          and ₹500 verification fee have been received. Our team will review your application
          within 2–3 working days.
        </p>

        {/* Reference Number */}
        <div className="mt-5 inline-flex items-center gap-3 bg-muted border border-border rounded-xl px-5 py-3">
          <Icon name="HashtagIcon" size={16} variant="outline" className="text-muted-foreground" />
          <div className="text-left">
            <p className="text-xs text-muted-foreground">Reference Number</p>
            <p className="text-sm font-700 text-foreground font-mono">FCEP-2026-08284731</p>
          </div>
        </div>
      </div>

      {/* Status Tracker */}
      <div className="bg-card border border-border rounded-2xl p-6 lg:p-8 mb-6">
        <h3 className="text-lg font-700 text-foreground mb-6 flex items-center gap-2">
          <Icon name="ClockIcon" size={20} variant="outline" className="text-primary" />
          Verification Status Tracker
        </h3>

        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-5 top-5 bottom-5 w-0.5 bg-border" />

          <div className="space-y-6">
            {statusSteps.map((step) => (
              <div key={step.id} className="relative flex items-start gap-5">
                {/* Status dot */}
                <div
                  className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    step.status === 'completed'
                      ? 'bg-secondary'
                      : step.status === 'current' ?'bg-primary' :'bg-muted border-2 border-border'
                  }`}
                >
                  {step.status === 'completed' ? (
                    <Icon name="CheckIcon" size={18} variant="outline" className="text-white" />
                  ) : step.status === 'current' ? (
                    <Icon
                      name={step.icon as Parameters<typeof Icon>[0]['name']}
                      size={18}
                      variant="outline"
                      className="text-white"
                    />
                  ) : (
                    <Icon
                      name={step.icon as Parameters<typeof Icon>[0]['name']}
                      size={18}
                      variant="outline"
                      className="text-muted-foreground"
                    />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 pb-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h4
                      className={`text-base font-700 ${
                        step.status === 'completed'
                          ? 'text-secondary'
                          : step.status === 'current' ?'text-primary' :'text-muted-foreground'
                      }`}
                    >
                      {step.label}
                    </h4>
                    <span
                      className={`text-xs font-600 px-2.5 py-1 rounded-full ${
                        step.status === 'completed'
                          ? 'status-verified'
                          : step.status === 'current' ?'status-review' :'status-pending'
                      }`}
                    >
                      {step.date}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* What Happens Next */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-6">
        <h3 className="text-base font-700 text-primary mb-4 flex items-center gap-2">
          <Icon name="InformationCircleIcon" size={18} variant="solid" className="text-primary" />
          What Happens Next?
        </h3>
        <ul className="space-y-3">
          {[
            { id: 'next-1', text: 'Our FCEP verification team will review your Aadhaar, PAN card, and social media profile within 2–3 working days.' },
            { id: 'next-2', text: 'You will receive an SMS and email confirmation at your registered mobile number and email address.' },
            { id: 'next-3', text: 'Once verified, your FCEP Verified Member badge will be activated and you can access all skill development programs.' },
            { id: 'next-4', text: 'Your FCEP developer will contact you to begin your skill development journey.' },
          ].map((item) => (
            <li key={item.id} className="flex items-start gap-3 text-sm text-blue-800">
              <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-primary flex-shrink-0 mt-0.5" />
              {item.text}
            </li>
          ))}
        </ul>
      </div>

      {/* Contact & CTA */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Icon name="PhoneIcon" size={18} variant="outline" className="text-primary" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Questions? Call us</p>
            <a href="tel:9007493290" className="text-primary font-700 text-sm hover:underline">
              +91 9007493290
            </a>
          </div>
        </div>

        <Link
          href="/"
          className="btn-primary justify-center"
          style={{ padding: '0.875rem 1.5rem' }}
        >
          <Icon name="HomeIcon" size={18} variant="outline" />
          Back to Homepage
        </Link>
      </div>
    </div>
  );
}